// Ringmenuen: knapper i en bue om det, man trykkede på. Gråtonede valg siger hvorfor.
// Knapperne står altid med mindst 8 px luft, og farlige valg (Riv ned) kræver et tryk mere.

import { h, setText } from './dom';

export interface RingItem {
  icon: string;
  label: string;
  /** Pris eller grund. */
  sub: string;
  enabled: boolean;
  /** Fremhævet (det oplagte valg). */
  primary?: boolean;
  /** Farligt valg: diskret rød kant, og første tryk spørger "Sikker?". */
  danger?: boolean;
  run(): void;
}

/** Var sidste input en tast (ikke mus eller finger)? Så får menuens første knap fokus. */
let byKeyboard = false;
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', () => (byKeyboard = true), true);
  window.addEventListener('pointerdown', () => (byKeyboard = false), true);
}

const BTN_W = 96;
const BTN_H = 80;
const GAP = 8;

/**
 * Placér `n` knapper i en bue om (cx, cy), så ingen to knapper er tættere end GAP. Ren funktion.
 * `angle` drejer buens midte (radianer); til en ledning peger den ud ad ledningens normal, og buen
 * holdes så smal, at ingen knap ligger oven på selve ledningen.
 */
export function ringLayout(n: number, up: boolean, angle?: number, full = false): { R: number; pts: { x: number; y: number }[] } {
  const mid = angle ?? (up ? -Math.PI / 2 : Math.PI / 2);
  // `full`: knapperne går hele vejen rundt om byen. Smallere end en bue, så den passer på en telefon.
  const maxSpan = full ? (Math.PI * 2 * (n - 1)) / n : angle === undefined ? Math.PI * 0.95 : Math.PI * 0.62;
  const span = n <= 1 ? 0 : full ? maxSpan : Math.min(maxSpan, 1.05 * (n - 1));
  const at = (R: number): { x: number; y: number }[] => {
    const out: { x: number; y: number }[] = [];
    for (let i = 0; i < n; i++) {
      const a = n === 1 ? mid : mid - span / 2 + (span * i) / (n - 1);
      out.push({ x: Math.cos(a) * R, y: Math.sin(a) * R });
    }
    return out;
  };
  const clear = (pts: { x: number; y: number }[]): boolean => {
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const a = pts[i] as { x: number; y: number };
        const b = pts[j] as { x: number; y: number };
        if (Math.abs(a.x - b.x) < BTN_W + GAP && Math.abs(a.y - b.y) < BTN_H + GAP) return false;
      }
    }
    return true;
  };
  let R = n <= 1 ? 70 : 80;
  let pts = at(R);
  while (!clear(pts) && R < 260) {
    R += 4;
    pts = at(R);
  }
  return { R, pts };
}

/** Det frie felt, menuen skal holde sig inde i (sidekoordinater), og punkter den helst ikke dækker. */
export interface RingArea {
  left: number;
  top: number;
  right: number;
  bottom: number;
  avoid?: { x: number; y: number }[];
  /** Alle byer og værker: en knap oven på en af dem koster lidt, så næste træk kan starte på byen. */
  nodes?: { x: number; y: number }[];
  /** Et kort, der står åbent (sidekoordinater): menuen lægger sig helst ved siden af det (IGN-løkke runde 3). */
  block?: { left: number; top: number; right: number; bottom: number };
}

export class RingMenu {
  readonly el = h('div', { class: 'ring', role: 'menu', hidden: true });
  private openAt: { x: number; y: number } | null = null;
  onClose: (() => void) | null = null;
  /**
   * IGN-løkke 2: én gestus, én handling. Et træk, der starter på en knap (ringen står tit oven på
   * nabobyen), er ikke et tryk på knappen: ringen lukker og giver trækket videre til kortet, der
   * behandler det, som om fingeren var landet dér (`down`) og nu står ved `now`.
   */
  onDragThrough: ((down: PointerEvent, now: PointerEvent) => void) | null = null;
  private press: PointerEvent | null = null;

  constructor() {
    this.el.addEventListener('pointerdown', (e) => {
      this.press = (e.target as Element | null)?.closest('.ring-btn') ? e : null;
    });
    if (typeof window === 'undefined') return;
    window.addEventListener(
      'pointermove',
      (e) => {
        const d = this.press;
        if (!d || d.pointerId !== e.pointerId || !this.isOpen) return;
        if (Math.hypot(e.clientX - d.clientX, e.clientY - d.clientY) < 12) return;
        this.press = null;
        this.close();
        this.onDragThrough?.(d, e);
      },
      true,
    );
    const end = (e: PointerEvent): void => {
      if (this.press?.pointerId === e.pointerId) this.press = null;
    };
    window.addEventListener('pointerup', end, true);
    window.addEventListener('pointercancel', end, true);
  }

  get isOpen(): boolean {
    return this.openAt !== null;
  }

  /**
   * Åbn menuen om (x, y). `area` er det frie felt (mellem HUD og værktøjer): hele buen og titlen
   * holdes inde i det. Buen vender den vej, der skal flyttes mindst, og helst fri af byer med
   * mørkeur (`avoid`), så spilleren kan se den by, der er ved at gå ud.
   */
  open(x: number, y: number, title: string, items: RingItem[], area: RingArea, normal?: { x: number; y: number }): void {
    this.el.replaceChildren();
    this.openAt = { x, y };
    const n = items.length;
    const titleW = Math.min(260, title.length * 8.5 + 28);
    const titleH = 34;
    // Kandidater: opad og nedad, eller fra en ledning ud ad hver side af ledningens normal.
    const cands: { up: boolean; angle?: number; full?: boolean; spare?: boolean }[] = normal
      ? [
          { up: true, angle: Math.atan2(normal.y, normal.x) },
          { up: false, angle: Math.atan2(-normal.y, -normal.x) },
        ]
      : [{ up: true }, { up: false }];
    if (normal && normal.y > 0) cands.reverse();
    // Er buen bredere end skærmen (telefon, fem knapper om en by), går knapperne rundt om byen i
    // stedet, så ingen knap klippes af kanten (spor "coop", gæstens telefon).
    if (!normal && n >= 4) cands.push({ up: true, full: true });
    // IGN-løkke 2: en lodret ledning på en liggende telefon gav en bue (med titel) højere end skærmen,
    // og tredje knap stod under kanten. Den almindelige bue op eller ned er reserven: den kan dække
    // lidt af ledningen (det koster), men den klippes aldrig.
    if (normal) cands.push({ up: true, spare: true }, { up: false, spare: true });
    // IGN-løkke 2: hver bue må også skubbes lidt til siden, så den ikke dækker nabobyen (et træk fra
    // den by startede ellers på en knap). Skubbet koster det, det flytter, så midten vinder, når den er fri.
    const tries = cands.flatMap((c, ci) => [0, -56, 56, -112, 112].map((dx) => ({ ...c, ci, dx })));
    let best: { cost: number; up: boolean; R: number; pts: { x: number; y: number }[]; cx: number; cy: number; off: number } | null = null;
    tries.forEach((c) => {
      const ci = c.ci;
      const { R, pts } = ringLayout(n, c.up, c.angle, c.full);
      const up = c.angle === undefined ? c.up : Math.sin(c.angle) <= 0;
      const below = Math.max(0, ...pts.map((p) => (up ? p.y : -p.y) + BTN_H / 2));
      const off = Math.max(24, below + GAP);
      let x0 = Math.min(-titleW / 2, ...pts.map((p) => p.x - BTN_W / 2));
      let x1 = Math.max(titleW / 2, ...pts.map((p) => p.x + BTN_W / 2));
      let y0 = Math.min(...pts.map((p) => p.y - BTN_H / 2));
      let y1 = Math.max(...pts.map((p) => p.y + BTN_H / 2));
      if (up) y1 = Math.max(y1, off + titleH);
      else y0 = Math.min(y0, -off - titleH);
      x0 -= 4;
      x1 += 4;
      const fit = (lo: number, hi: number, a: number, b: number, v: number): number => {
        if (hi - lo > b - a) return (a + b) / 2 - (lo + hi) / 2;
        return Math.max(a - lo, Math.min(b - hi, v));
      };
      const cx = fit(x0, x1, area.left, area.right, x + c.dx);
      const cy = fit(y0, y1, area.top, area.bottom, y);
      let cost = Math.abs(cx - x) + Math.abs(cy - y) + ci * 6 + (c.full ? 160 : 0) + (c.spare ? 240 : 0);
      // Kan buen ikke være inden for feltet, klippes knapper af kanten: det er det dyreste af alt.
      if (x1 - x0 > area.right - area.left) cost += 2000;
      if (y1 - y0 > area.bottom - area.top) cost += 2000;
      // En by med mørkeur under en knap: dyrt. Den by skal spilleren kunne se.
      for (const a of area.avoid ?? []) {
        if (pts.some((p) => Math.abs(cx + p.x - a.x) < BTN_W / 2 + 22 && Math.abs(cy + p.y - a.y) < BTN_H / 2 + 22)) cost += 400;
      }
      for (const a of area.nodes ?? []) {
        if (Math.hypot(a.x - x, a.y - y) < 4) continue;
        if (pts.some((p) => Math.abs(cx + p.x - a.x) < BTN_W / 2 + 10 && Math.abs(cy + p.y - a.y) < BTN_H / 2 + 10)) cost += 90;
      }
      // Hændelseskortet: hver knap oven på det koster, så menuen vælger den side, der er fri.
      const b = area.block;
      if (b) {
        for (const p of pts) {
          if (cx + p.x + BTN_W / 2 > b.left && cx + p.x - BTN_W / 2 < b.right && cy + p.y + BTN_H / 2 > b.top && cy + p.y - BTN_H / 2 < b.bottom) cost += 300;
        }
        const ty = up ? cy + off : cy - off - titleH;
        if (cx + titleW / 2 > b.left && cx - titleW / 2 < b.right && ty + titleH > b.top && ty < b.bottom) cost += 300;
      }
      if (!best || cost < best.cost) best = { cost, up, R, pts, cx, cy, off };
    });
    const { up, R, pts, cx, cy, off } = best as unknown as { up: boolean; R: number; pts: { x: number; y: number }[]; cx: number; cy: number; off: number };
    // Titlen står på den anden side af prikken, fri af knapperne.
    const ttl = h('div', { class: 'ring-title', style: `left:${cx}px;top:${up ? cy + off : cy - off}px;--ty:${up ? '0' : '-100%'}` }, title);
    this.el.append(ttl);
    items.forEach((it, i) => {
      const p = pts[i] ?? { x: 0, y: -R };
      const sub = h('span', { class: 'ring-sub' }, it.sub);
      const label = h('span', { class: 'ring-label' }, it.label);
      let armed = false;
      let armedAt = 0;
      const btn = h(
        'button',
        {
          type: 'button',
          role: 'menuitem',
          class: `ring-btn${it.primary ? ' primary' : ''}${it.danger ? ' danger' : ''}`,
          // --ox/--oy: en tredjedel af vejen tilbage til prikken. Knappen folder sig ud derfra.
          style: `left:${cx + p.x}px;top:${cy + p.y}px;--d:${i * 30}ms;--ox:${Math.round((x - cx - p.x) * 0.3)}px;--oy:${Math.round((y - cy - p.y) * 0.3)}px`,
          'aria-disabled': it.enabled ? null : 'true',
          'aria-label': `${it.label}. ${it.sub}`,
          onclick: (e: Event) => {
            e.stopPropagation();
            if (!it.enabled) {
              btn.classList.remove('nope');
              void btn.offsetWidth;
              btn.classList.add('nope');
              return;
            }
            // Et dobbeltklik må aldrig rive ned: bekræftelsen tæller først efter et kort øjeblik (IGN runde 1:
            // et hurtigt dobbeltklik, hvor skjoldet plejede at stå, rev en ledning ned i finalen).
            if (it.danger && armed && performance.now() - armedAt < 450) return;
            if (it.danger && !armed) {
              armed = true;
              armedAt = performance.now();
              btn.classList.add('armed');
              setText(label, 'Sikker?');
              setText(sub, 'Tryk igen');
              btn.setAttribute('aria-label', `Sikker? Tryk igen for at ${it.label.toLowerCase()}.`);
              return;
            }
            this.close();
            it.run();
          },
        },
        h('span', { class: 'ring-icon', html: it.icon }),
        label,
        sub,
      );
      this.el.append(btn);
    });
    this.el.append(h('span', { class: 'ring-dot', style: `left:${x}px;top:${y}px` }));
    // Åbnet med tastaturet: ingen bevægelse. En tast skal føles øjeblikkelig, og man ser den ofte.
    this.el.classList.toggle('instant', byKeyboard);
    this.el.hidden = false;
    // Fokus på første knap kun, når menuen blev åbnet med tastaturet. Åbnet med mus eller finger
    // fik mellemrum (pause) ellers købt "Opgradér" (playtest).
    if (byKeyboard) (this.el.querySelector('.ring-btn:not([aria-disabled]):not(.danger)') as HTMLElement | null)?.focus({ preventScroll: true });
    else {
      this.el.tabIndex = -1;
      this.el.focus({ preventScroll: true });
    }
  }

  close(): void {
    if (!this.openAt) return;
    this.openAt = null;
    this.el.hidden = true;
    this.el.replaceChildren();
    this.onClose?.();
  }
}
